/**
 * MCP Bridge Service
 * 
 * Generic service for managing MCP server connections.
 * Works with ANY MCP server - no hardcoded service logic!
 * 
 * Architecture:
 * Frontend → MCPBridgeService → Backend Generic API → MCP Host → Any MCP Server
 */

import { apiClient } from './api';

// MCP Types
export interface MCPServer {
  id: string;
  mcp_server_name: string;
  server_url: string;
  transport_type: string;
  metadata?: {
    description?: string;
    [key: string]: any;
  };
  status: string;
  icon?: string;
}

export interface MCPConnection {
  connected: boolean;
  initialized: boolean;
  connection_id?: string;
  capabilities?: MCPCapabilities;
  auth_required?: boolean;
  auth_url?: string;
  error?: string;
}

export interface MCPCapabilities {
  tools: string[];
  resources: string[];
  prompts: string[];
  protocol_version: string;
}

export interface MCPInvokeParams {
  [key: string]: any;
}

export interface MCPInvokeResult {
  success: boolean;
  data?: any;
  error?: string;
}

/**
 * MCP Bridge Service Class
 * Provides a clean interface for MCP operations
 */
class MCPBridgeService {
  private readonly baseUrl = '/api/mcp';

  /**
   * Connect to an MCP server
   * Works with ANY MCP server - no service-specific logic
   */
  async connect(tenantId: string, mcpServerId: string): Promise<MCPConnection> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/connect`, {
        tenant_id: tenantId,
        mcp_server_id: mcpServerId
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to connect to MCP server');
      }
    } catch (error: any) {
      console.error('MCP connection error:', error);
      throw new Error(error.response?.data?.message || 'Failed to connect to MCP server');
    }
  }

  /**
   * Get connection status for an MCP server
   */
  async getStatus(tenantId: string, mcpServerId: string): Promise<MCPConnection> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/status`, {
        params: {
          tenant_id: tenantId,
          mcp_server_id: mcpServerId
        }
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        return {
          connected: false,
          initialized: false,
          error: response.data.message
        };
      }
    } catch (error: any) {
      console.error('MCP status check error:', error);
      return {
        connected: false,
        initialized: false,
        error: error.message || 'Failed to check MCP status'
      };
    }
  }

  /**
   * Disconnect from an MCP server
   */
  async disconnect(tenantId: string, mcpServerId: string): Promise<void> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/disconnect`, {
        tenant_id: tenantId,
        mcp_server_id: mcpServerId
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to disconnect from MCP server');
      }
    } catch (error: any) {
      console.error('MCP disconnect error:', error);
      throw new Error(error.response?.data?.message || 'Failed to disconnect from MCP server');
    }
  }

  /**
   * Invoke a tool on an MCP server
   * This is how we actually use MCP server functionality
   */
  async invoke(
    tenantId: string,
    mcpServerId: string,
    tool: string,
    params: MCPInvokeParams
  ): Promise<MCPInvokeResult> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/invoke`, {
        tenant_id: tenantId,
        mcp_server_id: mcpServerId,
        tool,
        params
      });

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.message || 'Tool invocation failed'
        };
      }
    } catch (error: any) {
      console.error('MCP invoke error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to invoke MCP tool'
      };
    }
  }

  /**
   * Discover capabilities of an MCP server
   * Dynamically finds out what tools/resources/prompts are available
   */
  async discoverCapabilities(tenantId: string, mcpServerId: string): Promise<MCPCapabilities | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/discover`, {
        params: {
          tenant_id: tenantId,
          mcp_server_id: mcpServerId
        }
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        console.error('Failed to discover capabilities:', response.data.message);
        return null;
      }
    } catch (error: any) {
      console.error('MCP discovery error:', error);
      return null;
    }
  }

  /**
   * Handle OAuth callback if MCP server requires OAuth
   * Generic handler - works with any OAuth-based MCP server
   */
  async handleOAuthCallback(
    tenantId: string,
    mcpServerId: string,
    code: string,
    state?: string
  ): Promise<MCPConnection> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/oauth/callback`, {
        tenant_id: tenantId,
        mcp_server_id: mcpServerId,
        code,
        state
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'OAuth callback failed');
      }
    } catch (error: any) {
      console.error('OAuth callback error:', error);
      throw new Error(error.response?.data?.message || 'OAuth callback failed');
    }
  }

  /**
   * Poll OAuth status during authentication process
   * Now uses the new Composio API GET /connected_accounts/{connection_id} 
   * instead of callback URL polling
   */
  async pollOAuthStatus(tenantId: string, mcpServerId: string): Promise<{
    completed: boolean;
    connected?: boolean;
    error?: string;
  }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/auth/poll`, {
        params: {
          tenant_id: tenantId,
          mcp_server_id: mcpServerId
        }
      });

      if (response.data.success) {
        return {
          completed: response.data.data.completed || false,
          connected: response.data.data.connected || false
        };
      } else {
        return {
          completed: false,
          error: response.data.message
        };
      }
    } catch (error: any) {
      // Don't log polling errors as they're expected during OAuth process
      return {
        completed: false,
        error: error.response?.data?.message || 'Failed to poll OAuth status'
      };
    }
  }

  /**
   * Get all available MCP servers from the registry
   */
  async getAvailableServers(): Promise<MCPServer[]> {
    try {
      const response = await apiClient.get('/api/external-tools/available');
      
      if (response.data.success) {
        // Filter to only show MCP servers with 'shown' status
        return response.data.data.filter((server: MCPServer) => server.status === 'shown');
      } else {
        console.error('Failed to fetch MCP servers:', response.data.message);
        return [];
      }
    } catch (error: any) {
      console.error('Error fetching MCP servers:', error);
      return [];
    }
  }

  /**
   * Get user's connected MCP servers
   */
  async getUserServers(tenantId: string): Promise<MCPServer[]> {
    try {
      const response = await apiClient.get(`/api/external-tools/user/${tenantId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        console.error('Failed to fetch user servers:', response.data.message);
        return [];
      }
    } catch (error: any) {
      console.error('Error fetching user servers:', error);
      return [];
    }
  }

  /**
   * Add an MCP server to user's collection
   */
  async addUserServer(tenantId: string, mcpServerId: string): Promise<void> {
    try {
      const response = await apiClient.post('/api/external-tools/user', {
        tenant_id: tenantId,
        mcp_server_id: mcpServerId,
        enabled: true
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to add MCP server');
      }
    } catch (error: any) {
      console.error('Error adding MCP server:', error);
      throw new Error(error.response?.data?.message || 'Failed to add MCP server');
    }
  }

  /**
   * Remove an MCP server from user's collection
   */
  async removeUserServer(tenantId: string, mcpServerId: string): Promise<void> {
    try {
      const response = await apiClient.delete(`/api/external-tools/user/${tenantId}/${mcpServerId}`);

      // Backend returns 204 No Content on successful deletion
      if (response.status !== 204 && response.status !== 200) {
        throw new Error(response.data?.message || 'Failed to remove MCP server');
      }
    } catch (error: any) {
      console.error('Error removing MCP server:', error);
      throw new Error(error.response?.data?.message || 'Failed to remove MCP server');
    }
  }

  /**
   * Test MCP server connectivity
   * Useful for debugging and health checks
   */
  async testConnection(mcpServerId: string): Promise<{
    reachable: boolean;
    latency?: number;
    error?: string;
  }> {
    try {
      const startTime = Date.now();
      const response = await apiClient.get(`${this.baseUrl}/test/${mcpServerId}`);
      const latency = Date.now() - startTime;

      if (response.data.success) {
        return {
          reachable: true,
          latency
        };
      } else {
        return {
          reachable: false,
          error: response.data.message
        };
      }
    } catch (error: any) {
      return {
        reachable: false,
        error: error.message || 'Connection test failed'
      };
    }
  }
}

// Export singleton instance
export const mcpBridgeService = new MCPBridgeService();

// Also export the class for testing
export { MCPBridgeService };